import { takeEvery, call, put, fork, select } from 'redux-saga/effects';
import { delay } from '../../shared/helpers/helpers';
import { loginService, getQuickSetupStateService } from '../../auth/services/authService';
import { enqueueSnackbar } from '../../app/actions/appActions';
import {
    COMPLETE_SETUP_REQUEST,
    GET_SETUP_STATE_REQUEST,
    RESEND_VERIFICATION_CODE_REQUEST,
    ACTIVATE_DENWA_NANO_REQUEST
} from '../actions/wizardActionTypes';
import {
    updateStep,
    completeSetup,
    completeSetupSuccess,
    completeSetupFailure,
    decreaseTimer,
    getSetupStateSuccess,
    getSetupStateFailure,
    resendVerificationCodeSuccess,
    resendVerificationCodeFailure,
    activateDenwaNanoSuccess,
    activateDenwaNanoFailure,
    setLicenceStatus
} from '../actions/wizardActions';
import { completeSetupService, resendVerificationCodeService, activateDenwaNanoService } from '../services/wizardServices';
import { getCompleted } from '../selectors/wizardSelectors';

/**
 * Finish the setup
 */
const finishSetupTask = function* (configuration) {
    try {
        let completedSetup = 1;
        let hashForQR = null;
        let urlSetupForQR = null;
        let licence = null;

        yield put(enqueueSnackbar({
            message: 'common:CHECKING_STATE',
            options: { variant: 'info' }
        }));

        do {
            yield delay(10000);

            const { data: { token } } = yield call(loginService, 'admin', 'admin', true);

            localStorage.setItem('token', token);

            const { data: { SetupInfo: { Status, Hash, UrlSetup, License } } } = yield call(getQuickSetupStateService, true);

            completedSetup = Status;
            hashForQR = Hash;
            urlSetupForQR = UrlSetup;
            licence = License;

            if (completedSetup === 2) {
                throw new Error();
            }
        } while (completedSetup !== 1);

        const completed = yield select(getCompleted);

        if (!completed) {
            const { General: { CompanyEmail: companyEmail } } = configuration;
            const formattedURL = `${window.location.protocol}//${window.location.hostname}${urlSetupForQR}`;

            yield put(setLicenceStatus(licence));

            if (licence === 412) {
                yield put(activateDenwaNanoSuccess(null));
            } else if (licence !== 200 && licence !== 412) {
                yield put(activateDenwaNanoFailure());
            }

            yield put(completeSetupSuccess(companyEmail));
            yield put(decreaseTimer(50));
            yield put(updateStep({
                Hash: hashForQR,
                URLSetup: formattedURL,
                Key: 'qr'
            }));
            yield put(enqueueSnackbar({
                message: 'common:FINISHED',
                options: { variant: 'success' }
            }));
        }
    } catch (error) {
        yield put(getSetupStateFailure());
        yield put(decreaseTimer(50));
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

/**
 * Start the setup
 *
 * @param {Object} action - Action that has the configuration
 */
const getSetupStateTask = function* (action) {
    try {
        const { data: { token } } = yield call(loginService, 'admin', 'admin', true);

        localStorage.setItem('token', token);

        let { data: { SetupInfo: { Status, Hash, UrlSetup, License } } } = yield call(getQuickSetupStateService, true);

        yield put(getSetupStateSuccess());

        switch (Status) {
            case 0:
            case 2: {
                const { configuration } = action.payload;

                yield put(completeSetup(configuration));

                break;
            }
            case 1: {
                const { configuration: { General: { CompanyEmail: companyEmail } } } = action.payload;
                const formattedURLReady = `${window.location.protocol}//${window.location.hostname}${UrlSetup}`;

                yield put(setLicenceStatus(License));

                if (License === 412) {
                    yield put(activateDenwaNanoSuccess(null));
                } else if (License !== 200 && License !== 412) {
                    yield put(activateDenwaNanoFailure());
                }

                yield put(completeSetupSuccess(companyEmail));
                yield put(decreaseTimer(50));
                yield put(updateStep({
                    Hash,
                    URLSetup: formattedURLReady,
                    Key: 'qr'
                }));
                yield put(enqueueSnackbar({
                    message: 'common:FINISHED',
                    options: { variant: 'success' }
                }));

                break;
            }
            case 3: {
                yield fork(finishSetupTask);

                break;
            }
            default:
                break;
        }
    } catch (error) {
        yield put(getSetupStateFailure());
        yield put(decreaseTimer(50));
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

/**
 * Continue with the setup
 *
 * @param {Object} action - Configuration
 */
const completeSetupTask = function* (action) {
    try {
        yield put(enqueueSnackbar({
            message: 'common:INITIALIZING',
            options: { variant: 'info' }
        }));

        const { configuration } = action.payload;
        const { data: { result } } = yield call(completeSetupService, configuration);

        if (result === 1) {
            yield put(enqueueSnackbar({
                message: 'common:RESTORING',
                options: { variant: 'info' }
            }));

            yield delay(30000);
            yield fork(finishSetupTask, configuration);
        }
    } catch (error) {
        yield put(completeSetupFailure());
        yield put(decreaseTimer(50));
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

/**
 * Resend verification code
 *
 * @param {Object} action - Contains the email destination
 */
const resendVerificationCodeTask = function* (action) {
    try {
        const { email } = action.payload;
        const { data: { result } } = yield call(resendVerificationCodeService, email);

        if (result === 1) {
            yield put(resendVerificationCodeSuccess(email));
            yield put(enqueueSnackbar({
                message: 'wizard:ACTIVATION_CODE_RESENT',
                options: { variant: 'success' }
            }));
        } else {
            throw new Error();
        }
    } catch (error) {
        yield put(resendVerificationCodeFailure());
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

/**
 * Activate Denwa Nano
 *
 * @param {Object} action - Contains the activation code
 */
const activateDenwaNanoTask = function* (action) {
    try {
        const { activationCode } = action.payload;

        const { data: { result } } = yield call(activateDenwaNanoService, activationCode);

        if (result === 1) {
            yield put(activateDenwaNanoSuccess(activationCode));
        } else {
            throw new Error();
        }
    } catch (error) {
        yield put(activateDenwaNanoFailure());
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

/**
 * Start the wizard saga
 */
export default function* wizardSaga() {
    yield takeEvery(GET_SETUP_STATE_REQUEST, getSetupStateTask);
    yield takeEvery(COMPLETE_SETUP_REQUEST, completeSetupTask);
    yield takeEvery(RESEND_VERIFICATION_CODE_REQUEST, resendVerificationCodeTask);
    yield takeEvery(ACTIVATE_DENWA_NANO_REQUEST, activateDenwaNanoTask);
}
